import { useCallback, useState, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Sidebar } from "@/components/layout/sidebar";
import { Header } from "@/components/layout/header";
import { MobileNav } from "@/components/layout/mobile-nav";
import { PushPrompt } from "@/components/notifications/push-prompt";
import { LoginPage } from "@/pages/auth/login";
import { DashboardPage } from "@/pages/tenant/dashboard";
import { LegalSettingsPage } from "@/pages/tenant/legal";
import { ProductListPage } from "@/pages/products/ProductList";
import { ProductFormPage } from "@/pages/products/ProductForm";
import { ProductImportPage } from "@/pages/products/ProductImport";
import { OrderListPage } from "@/pages/orders/OrderList";
import { OrderDetailPage } from "@/pages/orders/OrderDetail";
import { CouponListPage } from "@/pages/coupons/CouponList";
import { CouponFormPage } from "@/pages/coupons/CouponForm";
import { ShippingSettingsPage } from "@/pages/shipping/ShippingSettings";
import { SubscriptionListPage } from "@/pages/subscriptions/SubscriptionList";
import { PopupListPage } from "@/pages/popups/PopupList";
import { PopupFormPage } from "@/pages/popups/PopupForm";
import { ReviewListPage } from "@/pages/reviews/ReviewList";
import { BookingCalendarPage } from "@/pages/booking/BookingCalendar";
import { ServiceListPage as BookingServiceListPage } from "@/pages/booking/ServiceList";
import { AvailabilitySettingsPage } from "@/pages/booking/AvailabilitySettings";
import { AppointmentsPage } from "@/pages/booking/AppointmentsPage";
import NotificationSettings from "@/pages/settings/NotificationSettings";
import { BackupsPage } from "@/pages/super/BackupsPage";
import { SuperAdminLayout } from "@/pages/super/layout";
import { SuperDashboardPage } from "@/pages/super/dashboard";
import { SuperTenantsPage } from "@/pages/super/tenants";
import { SuperHealthPage } from "@/pages/super/health";
import { SuperSupportPage } from "@/pages/super/support";
import { ProvisioningPage } from "@/pages/super/provisioning";
import { BillingPage } from "@/pages/super/billing";
import { PartnersPage } from "@/pages/super/partners";
import { OnboardingPage } from "@/pages/onboarding/index";
import { FoxyPage } from "@/pages/foxy/index";
import { useAuth } from "@/hooks/use-auth";

function ProtectedRoute(): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-muted-foreground">
          {"\u05D8\u05D5\u05E2\u05DF..."}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function PublicRoute(): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-muted-foreground">
          {"\u05D8\u05D5\u05E2\u05DF..."}
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function AdminLayout(): ReactNode {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleMenuClick = useCallback(() => {
    setMobileNavOpen(true);
  }, []);

  return (
    <div className="flex min-h-dvh">
      <aside className="hidden md:block border-e">
        <div className="sticky top-0 h-dvh overflow-y-auto">
          <Sidebar />
        </div>
      </aside>

      <MobileNav open={mobileNavOpen} onOpenChange={setMobileNavOpen} />

      <div className="flex flex-1 flex-col">
        <Header onMenuClick={handleMenuClick} />
        <PushPrompt />
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function PlaceholderPage({ title }: { readonly title: string }): ReactNode {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <p className="text-lg text-muted-foreground">{title}</p>
    </div>
  );
}

export function App(): ReactNode {
  return (
    <TooltipProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route element={<AdminLayout />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/orders" element={<OrderListPage />} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/products" element={<ProductListPage />} />
              <Route path="/products/new" element={<ProductFormPage />} />
              <Route path="/products/import" element={<ProductImportPage />} />
              <Route path="/products/:id" element={<ProductFormPage />} />
              <Route path="/coupons" element={<CouponListPage />} />
              <Route path="/coupons/new" element={<CouponFormPage />} />
              <Route path="/coupons/:id" element={<CouponFormPage />} />
              <Route path="/shipping" element={<ShippingSettingsPage />} />
              <Route path="/subscriptions" element={<SubscriptionListPage />} />
              <Route path="/popups" element={<PopupListPage />} />
              <Route path="/popups/new" element={<PopupFormPage />} />
              <Route path="/popups/:id" element={<PopupFormPage />} />
              <Route path="/reviews" element={<ReviewListPage />} />
              <Route path="/booking/calendar" element={<BookingCalendarPage />} />
              <Route path="/booking/appointments" element={<AppointmentsPage />} />
              <Route path="/booking/services" element={<BookingServiceListPage />} />
              <Route path="/booking/settings" element={<AvailabilitySettingsPage />} />
              <Route path="/foxy" element={<FoxyPage />} />
              <Route path="/settings/notifications" element={<NotificationSettings />} />
              <Route
                path="/settings"
                element={
                  <PlaceholderPage
                    title={"\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA"}
                  />
                }
              />
              <Route
                path="/team"
                element={
                  <PlaceholderPage
                    title={"\u05E6\u05D5\u05D5\u05EA"}
                  />
                }
              />
              <Route path="/legal" element={<LegalSettingsPage />} />
              <Route
                path="/billing"
                element={
                  <PlaceholderPage
                    title={"\u05D7\u05D9\u05D5\u05D1"}
                  />
                }
              />
            </Route>

            <Route element={<SuperAdminLayout />}>
              <Route path="/super/dashboard" element={<SuperDashboardPage />} />
              <Route path="/super/tenants" element={<SuperTenantsPage />} />
              <Route path="/super/provisioning" element={<ProvisioningPage />} />
              <Route path="/super/billing" element={<BillingPage />} />
              <Route path="/super/partners" element={<PartnersPage />} />
              <Route path="/super/health" element={<SuperHealthPage />} />
              <Route path="/super/support" element={<SuperSupportPage />} />
              <Route path="/super/backups" element={<BackupsPage />} />
              <Route path="/super" element={<Navigate to="/super/dashboard" replace />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
}
